import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { gigData } from './App';
import IndexGrid from './components/IndexGrid';
import './styles/GalleryPage.css';
import './styles/IndexGrid.css';

function GalleryPage() {
  const { gigId } = useParams();
  const [gig, setGig] = useState(null);
  const [activeCategory, setActiveCategory] = useState('originals');
  const [cursorSide, setCursorSide] = useState('none');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageCount, setImageCount] = useState({});
  const [isIndexViewActive, setIsIndexViewActive] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const audioRef = useRef(null);

  useEffect(() => {
    const currentGig = gigData.find(g => g.id === parseInt(gigId));
    if (currentGig) {
      setGig(currentGig);
      setActiveCategory('originals');
      setCurrentImageIndex(0);
      setIsIndexViewActive(false);
      // Update image counts based on actual arrays
      setImageCount({
        originals: currentGig.images.originals.length,
        bw: currentGig.images.bw.length
      });
    }
  }, [gigId]);

  // Handle audio play/stop
  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Auto-play when component mounts
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.play().catch(error => {
        console.log("Auto-play prevented:", error);
        setIsPlaying(false);
      });
    }
  }, [gig]);

  const handleMouseMove = (event) => {
    if (event.clientX < window.innerWidth / 2) {
      setCursorSide('left');
    } else {
      setCursorSide('right');
    }
  };

  const handleMouseLeave = () => {
    setCursorSide('none');
  };

  const changeCategory = (category) => {
    setActiveCategory(category);
    setCurrentImageIndex(0);
    setIsIndexViewActive(false);
  };

  // Reset image index when category changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [activeCategory]);

  const handleImageNavigation = () => {
    if (isIndexViewActive) return;
    const count = imageCount[activeCategory] || 0;
    if (count <= 1) return;

    if (cursorSide === 'left') {
      setCurrentImageIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : count - 1));
    } else if (cursorSide === 'right') {
      setCurrentImageIndex((prevIndex) => (prevIndex < count - 1 ? prevIndex + 1 : 0));
    }
  };

  const toggleIndexView = () => {
    setIsIndexViewActive(prev => !prev);
  };

  // Add stopPropagation to prevent image navigation when clicking audio controls
  const handleAudioClick = (e) => {
    e.stopPropagation();
    toggleAudio();
  };

  if (!gig) {
    return <div>Loading gig data or gig not found...</div>;
  }

  const currentImageFilename = `${currentImageIndex + 1}.${activeCategory === 'cover' ? 'JPG' : 'jpg'}`;
  const imagePath = `${gig.galleryBasePath}/${activeCategory}/${currentImageFilename}`;
  const totalImagesInCategory = imageCount[activeCategory] || 0;

  const allGalleryImages = [
    ...(gig?.images?.originals?.map(img => ({ src: img, type: 'originals' })) || []),
    ...(gig?.images?.bw?.map(img => ({ src: img, type: 'bw' })) || [])
  ];

  return (
    <div
      className={`gallery-page-container ${!isIndexViewActive ? `cursor-${cursorSide}` : ''}`}
      onMouseMove={!isIndexViewActive ? handleMouseMove : undefined}
      onMouseLeave={!isIndexViewActive ? handleMouseLeave : undefined}
      onClick={!isIndexViewActive ? handleImageNavigation : undefined}
    >
      {isIndexViewActive ? (
        <IndexGrid images={allGalleryImages} closeView={toggleIndexView} />
      ) : (
        <>
          <header className="gallery-header">
            <div className="gallery-nav-left">
              <Link to="/" className="back-link">luna sahin</Link>
            </div>
            <div className="gallery-categories">
              <span
                className={activeCategory === 'originals' ? 'active' : ''}
                onClick={() => changeCategory('originals')}
              >
                originals
              </span>
              <span
                className={activeCategory === 'bw' ? 'active' : ''}
                onClick={() => changeCategory('bw')}
              >
                b&w
              </span>
            </div>
            <div className="gallery-nav-right">
              <span onClick={toggleIndexView} style={{cursor: 'pointer'}}>Index</span>
            </div>
          </header>

          <div className="gallery-image-area">
            {totalImagesInCategory > 0 ? (
              <img
                key={`${activeCategory}-${currentImageIndex}`}
                src={process.env.PUBLIC_URL + imagePath}
                alt={`${gig.bandName} - ${activeCategory} ${currentImageIndex + 1}`}
                className="gallery-main-image"
                onError={(e) => { console.error(`Failed to load image: ${imagePath}`); e.target.style.display = 'none'; }}
              />
            ) : (
              <p>No images available in this category.</p>
            )}
          </div>

          <footer className="gallery-footer">
            <div className="photo-count">
              {String(currentImageIndex + 1).padStart(2, '0')} of {String(totalImagesInCategory).padStart(2, '0')}
            </div>
            <div className="band-info">
              {`${gig.bandName}, ${gig.venue}, ${gig.year}`}
            </div>
            <div className="gallery-audio" onClick={handleAudioClick}>
              <audio
                ref={audioRef}
                src={process.env.PUBLIC_URL + `/audio/${gig.id}.mp3`}
                loop
              />
              <button 
                className={`audio-toggle ${isPlaying ? 'playing' : ''}`}
                aria-label={isPlaying ? 'Stop' : 'Play'}
              >
                {isPlaying ? 'stop the tunes' : 'turn on the tunes'}
              </button>
            </div>
          </footer>
        </>
      )}
    </div>
  );
}

export default GalleryPage; 